// Configuration for priority dealers and regions
export const priorityDealerConfig = {
	// Region boundaries with their coordinates
	regions: {
		'western-australia': {
			name: 'Western Australia',
			bounds: {
				north: -13.0,
				south: -35.0,
				east: 129.0,
				west: 113.0,
			},
		},
		'new-south-wales': {
			name: 'New South Wales',
			bounds: {
				north: -28.0,
				south: -37.5,
				east: 153.5,
				west: 141.0,
			},
		},
		'victoria': {
			name: 'Victoria',
			bounds: {
				north: -33.0,
				south: -39.0,
				east: 150.0,
				west: 140.0,
			},
		},
		'queensland': {
			name: 'Queensland',
			bounds: {
				north: -9.0,
				south: -29.0,
				east: 153.5,
				west: 138.0,
			},
		},
		'south-australia': {
			name: 'South Australia',
			bounds: {
				north: -26.0,
				south: -38.0,
				east: 141.0,
				west: 129.0,
			},
		},
		'tasmania': {
			name: 'Tasmania',
			bounds: {
				north: -39.0,
				south: -43.5,
				east: 148.0,
				west: 143.0,
			},
		},
		'northern-territory': {
			name: 'Northern Territory',
			bounds: {
				north: -10.0,
				south: -26.0,
				east: 138.0,
				west: 129.0,
			},
		},
		'australian-capital-territory': {
			name: 'Australian Capital Territory',
			bounds: {
				north: -35.0,
				south: -35.9,
				east: 149.4,
				west: 148.8,
			},
		},
	},

	// Priority dealers configuration
	priorityDealers: [
		{
			id: 'pool-robotics-perth',
			name: 'Pool Robotics Perth',
			searchPattern: 'pool robotics perth',
			regions: ['western-australia'],
			dealerTypes: ['roboticrepairs'],
			priority: 1, // Higher number = higher priority
			forceTop: true, // Always show at top when conditions are met
		},
		{
			id: 'poolwerx-geelong',
			name: 'Poolwerx Geelong',
			searchPattern: 'poolwerx geelong',
			regions: ['victoria'],
			dealerTypes: ['authoriseddealer', 'roboticrepairs'],
			priority: 2,
			forceTop: true,
		},
		{
			id: 'pooltronics',
			name: 'Pooltronics',
			searchPattern: 'pooltronics',
			regions: ['victoria'],
			dealerTypes: ['roboticrepairs', 'elite'],
			priority: 1,
			forceTop: false,
		}
		// Add more priority dealers here as needed
		// Example:
		// {
		//   id: 'another-priority-dealer',
		//   name: 'Another Priority Dealer',
		//   searchPattern: 'another priority dealer',
		//   regions: ['new-south-wales', 'victoria'],
		//   dealerTypes: ['elite', 'authoriseddealer'],
		//   priority: 2,
		//   forceTop: false
		// }
	],
}

// Helper function to check if coordinates are within a region
export const isInRegion = (coordinates, regionKey) => {
	const [lng, lat] = coordinates
	const region = priorityDealerConfig.regions[regionKey]

	if (!region) return false

	const { bounds } = region
	return (
		lng >= bounds.west &&
		lng <= bounds.east &&
		lat >= bounds.south &&
		lat <= bounds.north
	)
}

/**
 * True when the UI / GraphQL search type should apply this priority dealer row.
 * Supports string types, array of strings, and Mongo-style `{ $in: ['a','b'] }` (e.g. Spare Parts).
 */
export const searchTypeMatchesDealerTypes = (searchType, dealerTypes) => {
	if (searchType == null || !dealerTypes?.length) return false
	if (Array.isArray(searchType)) {
		return searchType.some(t => dealerTypes.includes(t))
	}
	if (typeof searchType === 'object' && Array.isArray(searchType.$in)) {
		return searchType.$in.some(t => dealerTypes.includes(t))
	}
	if (typeof searchType === 'string') {
		return dealerTypes.includes(searchType)
	}
	return false
}

// Helper function to get applicable priority dealers for a location and search type
export const getApplicablePriorityDealers = (coordinates, searchType) => {
	const applicableDealers = []

	// Check each priority dealer
	priorityDealerConfig.priorityDealers.forEach(dealer => {
		// Check if user is in any of the dealer's regions
		const isInApplicableRegion = dealer.regions.some(regionKey =>
			isInRegion(coordinates, regionKey)
		)

		const isMatchingType = searchTypeMatchesDealerTypes(
			searchType,
			dealer.dealerTypes
		)

		if (isInApplicableRegion && isMatchingType) {
			applicableDealers.push({
				...dealer,
				applicableRegions: dealer.regions.filter(regionKey =>
					isInRegion(coordinates, regionKey)
				),
			})
		}
	})

	// Sort by priority (higher number = higher priority)
	return applicableDealers.sort((a, b) => b.priority - a.priority)
}

// Helper function to check if a dealer name matches a priority dealer pattern
export const matchesPriorityDealer = (dealerName, priorityDealer) => {
	if (!dealerName || !priorityDealer.searchPattern) return false
	return dealerName
		.toLowerCase()
		.includes(priorityDealer.searchPattern.toLowerCase())
}
